import { atom, computed } from 'nanostores';
import { pricingStore } from './pricing';
import { proPricingPlansStore } from './proPlans';
import { teamPricingPlansStore } from './teamPlans';
import type { PricingWorkspace } from './types';
import { chatStore } from '~/lib/stores/chat';
import { userStore } from '~/lib/stores/user';
import type { OrganizationData } from '~/types/organization';
import type { UserData } from '~/types/user';
import { hmrPropertyFactory } from '~/utils/hmr';

const freeWorkspace: PricingWorkspace = {
  id: 'free',
  label: 'Free - Personal',
  type: 'free',
};

const hmr = hmrPropertyFactory('WorkspacesStore');

export class WorkspacesStore {
  @hmr()
  workspaces = computed([userStore.user, userStore.selectableOrganizations], (user) => {
    if (!user) {
      return [freeWorkspace];
    }

    const result: PricingWorkspace[] = [getPersonalWorkspace(user)];

    const orgWorkspaces = user.organizations?.map((org) => getTeamWorkspace(org));

    if (orgWorkspaces && orgWorkspaces.length > 0) {
      result.push(...orgWorkspaces);
    }

    if (this.selectedWorkspace.get().type !== 'free') {
      return result;
    }

    if (user.activeOrganizationId) {
      this.selectWorkspace(
        result.find((workspace) => workspace.type === 'team' && workspace.org.id === user.activeOrganizationId)!,
      );
    } else {
      this.selectWorkspace(result[0]);
    }

    return result;
  });

  @hmr()
  shouldDisplayWorkspaceSelector = computed([this.workspaces], (workspaces) => {
    /**
     * We only display workspace selector when user has one or more organizations
     * if user has only personal account, or there is no user at all (user is not logged in), we hide it.
     */

    return workspaces.length > 1;
  });

  @hmr()
  selectedWorkspace = atom<PricingWorkspace>(freeWorkspace);

  _selectWorkspace(workspace: PricingWorkspace) {
    this.selectedWorkspace.set(workspace);

    const selectableOrganizations = userStore.selectableOrganizations.get();

    if (workspace.type === 'team' && workspace.org.billing?.subscription?.interval) {
      pricingStore.selectedInterval.set(workspace.org.billing.subscription.interval);
    }

    if (workspace.type === 'personal' && workspace.user.membership?.subscription?.interval) {
      pricingStore.selectedInterval.set(workspace.user.membership.subscription.interval);
    }

    const chatStarted = chatStore.started.get();

    if (chatStarted) {
      /**
       * We don't sync selected workspace with user/activeOrg when user is chatting
       * or else it will trigger page reload and user will be kicked out of chat.
       */
      return;
    }

    if (workspace.type === 'personal') {
      userStore.setActiveOrganization(null, false);
    }

    if (workspace.type === 'team' && selectableOrganizations.some((org) => org.id === workspace.org.id)) {
      userStore.setActiveOrganization(workspace.org, false);
    }
  }

  selectWorkspace(workspace: PricingWorkspace) {
    if (workspace.type === 'personal') {
      workspacesStore.selectPersonalWorkspace(workspace.user);
    }

    if (workspace.type === 'team') {
      workspacesStore.selectTeamWorkspace(workspace.org);
    }
  }

  selectPersonalWorkspace(user: UserData) {
    this._selectWorkspace(getPersonalWorkspace(user));

    proPricingPlansStore.initActiveState(user);
    teamPricingPlansStore.initInactiveState();
  }

  selectTeamWorkspace(org: OrganizationData) {
    this._selectWorkspace(getTeamWorkspace(org));

    proPricingPlansStore.initInactiveState('You’re on Teams');
    teamPricingPlansStore.initActiveState(org);
  }
}

export const workspacesStore = new WorkspacesStore();

pricingStore.showPricingOverlay.subscribe((showPricingOverlay) => {
  if (!showPricingOverlay) {
    return;
  }

  const user = userStore.user.get();
  const activeOrg = userStore.getActiveOrganization().get();

  if (activeOrg) {
    workspacesStore.selectTeamWorkspace(activeOrg);
  } else if (user) {
    workspacesStore.selectPersonalWorkspace(user);
  }
});

if (import.meta.env.DEV) {
  Object.assign(globalThis, { workspacesStore });
}

function getPersonalWorkspace(user: UserData): PricingWorkspace {
  return {
    type: 'personal',
    id: user.username,
    label: 'Pro - Personal',
    user,
    account: {
      accountName: user.name ?? user.username,
      type: 'user',
      avatar: user.avatar,
      membershipLevel: user.membership?.tier,
      user,
    },
  };
}

function getTeamWorkspace(org: OrganizationData): PricingWorkspace {
  return {
    type: 'team',
    id: org.id.toString(),
    label: `Teams - ${org.name}`,
    org,
    account: {
      accountName: org.name,
      type: 'organization',
      avatar: org.avatar,
      membershipLevel: org.billing?.tier,
    },
  };
}
